import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { Typography } from '@mui/joy';
import { useTranslation } from 'react-i18next';
import { TypeDefinitionAttribute } from '../../codegen/graphql';
import { FormikHelpersRef } from '../../types';
import InstalledBaseTDAForm from '../InstalledBaseTDAForm';
import WizardStepper from '../Wizard/WizardStepper';
import WizardNavigation from '../Wizard/WizardNavigation';
import { WizardStepConfig } from '../Wizard/types';
import { NO_CATEGORY_KEY, TDAStepperFormProps } from './types';

const deriveStepKeys = (attributes: TypeDefinitionAttribute[]): string[] => {
    const seen = new Set<string>();
    const order: string[] = [];
    let hasUncategorized = false;

    for (const attr of attributes) {
        const cats = (attr.categories ?? []).filter((c): c is string => typeof c === 'string' && c.length > 0);
        if (cats.length === 0) {
            hasUncategorized = true;
            continue;
        }
        for (const c of cats) {
            if (!seen.has(c)) {
                seen.add(c);
                order.push(c);
            }
        }
    }

    const initial = hasUncategorized ? [NO_CATEGORY_KEY, ...order] : order;

    // Hoist categories that contain a single installedBaseNodePicker attribute to the front
    // (after any synthetic `_`-prefixed step like the event-type tile picker) so the
    // relocation/target node selection is always step 1 for the user.
    // NO_CATEGORY_KEY also starts with `_` but it represents uncategorized attrs — keep it
    // out of the synthetic bucket so injected steps (e.g. `_targetNode`) still come first.
    const synthetic: string[] = [];
    const pickerOnly: string[] = [];
    const rest: string[] = [];
    for (const key of initial) {
        if (key !== NO_CATEGORY_KEY && key.startsWith('_')) {
            synthetic.push(key);
            continue;
        }
        const stepAttrs = filterAttributesForStep(attributes, key);
        const isPickerOnly =
            stepAttrs.length === 1 &&
            (stepAttrs[0] as { inputType?: string | null }).inputType === 'installedBaseNodePicker';
        if (isPickerOnly) {
            pickerOnly.push(key);
        } else {
            rest.push(key);
        }
    }
    return [...synthetic, ...pickerOnly, ...rest];
};

const filterAttributesForStep = (
    attributes: TypeDefinitionAttribute[],
    stepKey: string
): TypeDefinitionAttribute[] => {
    if (stepKey === NO_CATEGORY_KEY) {
        return attributes.filter((a) => {
            const cats = (a.categories ?? []).filter((c) => typeof c === 'string' && c.length > 0);
            return cats.length === 0;
        });
    }
    return attributes.filter((a) => (a.categories ?? []).includes(stepKey));
};

const TDAStepperForm = ({
    attributes,
    onSubmit,
    initialFormValues,
    stepSeedValues,
    finishButtonText,
    defaultCategoryLabel,
    categoryLabels,
    isSubmitting,
    onStepValuesChange,
    hideStepper,
    headerContent,
    customInputRenderers,
    onBeforeNext,
    initialActiveStep = 0,
    expectedTotalSteps,
    stepsFinalized = true,
    isNextLoading,
    onFormikHelpersRef,
    nodePickerRootNodeId,
}: TDAStepperFormProps) => {
    const { t } = useTranslation();

    const stepKeys = useMemo(() => deriveStepKeys(attributes), [attributes]);

    const labelFor = useCallback(
        (key: string): string => {
            if (key === NO_CATEGORY_KEY) {
                return defaultCategoryLabel ?? t('General');
            }
            const raw = categoryLabels?.[key] ?? key;
            return raw.charAt(0).toUpperCase() + raw.slice(1);
        },
        [categoryLabels, defaultCategoryLabel, t]
    );

    const steps: WizardStepConfig[] = useMemo(
        () =>
            stepKeys.map((key, idx) => ({
                id: idx,
                title: labelFor(key),
                category: key,
            })),
        [stepKeys, labelFor]
    );

    // While the real step count isn't settled yet (e.g. the install-event flow
    // before the picked node's TDAs have loaded), show the expected total so the
    // progress bar / Next-vs-Finish don't flicker through a transient count.
    const navTotal = stepsFinalized
        ? steps.length
        : Math.max(steps.length, expectedTotalSteps ?? steps.length);

    const initialFormDataPerStep = useMemo(() => {
        const data: Record<string, Record<string, any>> = {};
        for (const key of stepKeys) {
            data[key] = { ...(initialFormValues ?? {}) };
        }
        return data;
    }, [stepKeys, initialFormValues]);

    const [activeStep, setActiveStep] = useState(initialActiveStep);
    const [highestStepReached, setHighestStepReached] = useState(initialActiveStep);
    const [formData, setFormData] = useState<Record<string, Record<string, any>>>(initialFormDataPerStep);

    const formikRef = useRef<FormikHelpersRef | null>(null);

    const captureCurrentStep = useCallback(async (validate: boolean): Promise<{ ok: boolean; values: Record<string, any> }> => {
        if (!formikRef.current) return { ok: true, values: {} };

        if (validate) {
            const errors = await formikRef.current.validate();
            if (Object.keys(errors).length > 0) {
                await formikRef.current.submit();
                return { ok: false, values: {} };
            }
        }

        const values = formikRef.current.getValues();
        return { ok: true, values };
    }, []);

    const persistStep = useCallback((stepKey: string, values: Record<string, any>) => {
        setFormData((prev) => ({ ...prev, [stepKey]: values }));
    }, []);

    const handleNext = useCallback(async () => {
        const currentKey = stepKeys[activeStep];
        if (onBeforeNext) {
            try {
                await onBeforeNext(currentKey);
            } catch {
                return;
            }
        }
        const { ok, values } = await captureCurrentStep(true);
        if (!ok) return;

        persistStep(currentKey, values);

        const nextStep = activeStep + 1;
        if (nextStep < steps.length) {
            setActiveStep(nextStep);
            setHighestStepReached((prev) => Math.max(prev, nextStep));
        }
    }, [activeStep, stepKeys, steps.length, captureCurrentStep, persistStep, onBeforeNext]);

    const handleBack = useCallback(async () => {
        if (activeStep === 0) return;
        const currentKey = stepKeys[activeStep];
        const { values } = await captureCurrentStep(false);
        persistStep(currentKey, values);
        setActiveStep((prev) => prev - 1);
    }, [activeStep, stepKeys, captureCurrentStep, persistStep]);

    const handleStepClick = useCallback(
        async (targetStep: number) => {
            if (targetStep === activeStep) return;
            const currentKey = stepKeys[activeStep];

            if (targetStep < activeStep) {
                const { values } = await captureCurrentStep(false);
                persistStep(currentKey, values);
                setActiveStep(targetStep);
                return;
            }

            const { ok, values } = await captureCurrentStep(true);
            if (!ok) return;
            persistStep(currentKey, values);
            setActiveStep(targetStep);
            setHighestStepReached((prev) => Math.max(prev, targetStep));
        },
        [activeStep, stepKeys, captureCurrentStep, persistStep]
    );

    const handleFinish = useCallback(async () => {
        const currentKey = stepKeys[activeStep];
        const { ok, values } = await captureCurrentStep(true);
        if (!ok) return;

        const merged = stepKeys.reduce<Record<string, any>>((acc, key) => {
            const slice = key === currentKey ? values : formData[key] ?? {};
            return { ...acc, ...slice };
        }, {});

        await onSubmit(merged);
    }, [activeStep, stepKeys, formData, captureCurrentStep, onSubmit]);

    const currentStepKey = stepKeys[activeStep];
    const currentAttributes = useMemo(
        () => {
            const stepAttrs = filterAttributesForStep(attributes, currentStepKey ?? '')
                .map((a) => ({ ...a, categories: undefined }));

            // When a step has a single installedBaseNodePicker attribute, promote it to the
            // inline browser variant so the extra space is used by the navigable list rather
            // than a popup select.
            if (
                stepAttrs.length === 1 &&
                (stepAttrs[0] as any).inputType === 'installedBaseNodePicker'
            ) {
                return [{ ...stepAttrs[0], inputType: 'installedBaseNodeBrowser' }];
            }

            return stepAttrs;
        },
        [attributes, currentStepKey]
    );

    const handleStepValuesChange = useCallback(
        (values: Record<string, any>) => {
            if (onStepValuesChange && currentStepKey) {
                onStepValuesChange(currentStepKey, values);
            }
        },
        [onStepValuesChange, currentStepKey]
    );

    if (stepKeys.length === 0) {
        return (
            <Typography level="body-md" sx={{ color: 'text.secondary', p: 2 }}>
                {t('No attributes available')}
            </Typography>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {headerContent !== undefined
                ? headerContent
                : (!hideStepper && steps.length > 1 && (
                    <WizardStepper
                        steps={steps}
                        activeStep={activeStep}
                        highestStepReached={highestStepReached}
                        onStepClick={handleStepClick}
                        compact
                    />
                ))}

            <Box sx={{ pb: 1 }}>
                <InstalledBaseTDAForm
                    key={currentStepKey}
                    attributes={currentAttributes}
                    // Seeds merge under the cached step values on every render (not just
                    // at mount), so they also reach steps that appeared after mount —
                    // e.g. after an event-type switch reloaded the TDAs.
                    initialFormValues={{ ...(stepSeedValues ?? {}), ...(formData[currentStepKey] ?? {}) }}
                    setFormikHelpersRef={(ref) => {
                        formikRef.current = ref;
                        onFormikHelpersRef?.(ref);
                    }}
                    enableReinitialize={true}
                    onValuesChange={handleStepValuesChange}
                    customInputRenderers={customInputRenderers}
                    nodePickerRootNodeId={nodePickerRootNodeId}
                />
            </Box>

            <Box sx={{ pt: 1 }}>
                <WizardNavigation
                    activeStep={activeStep}
                    totalSteps={navTotal}
                    onBack={handleBack}
                    onNext={handleNext}
                    onFinish={handleFinish}
                    isNextLoading={isNextLoading}
                    isFinishLoading={isSubmitting}
                    finishButtonText={finishButtonText}
                />
            </Box>
        </Box>
    );
};

export default TDAStepperForm;
